import {
  RC_DURATION,
  ReebeloConditions,
  ReebeloCurrencies,
  ReebeloStoreT,
} from '@lambda/reebelo';
import { ProductVariant } from '@lambda/commons/apis/shopify/types/storefront';
import {
  CheckoutPaymentMethods,
  CheckoutT,
  CustomerOrderT,
} from '@lambda/apis/src/checkout/types';
import {
  CustomerSubscriptionStatus,
  CustomerSubscriptionSource,
} from '@lambda/apis/src/customer/enum';
import { CartLineT, CartT, NoteT } from '@lambda/apis/src/cart/types';
import { Customer, CustomerAddress } from '@lambda/apis/src/customer/types';
import { PaymentProcessor } from '@lambda/apis/src/checkout/enum';
import { OfferT, ProductT, SkuT } from '@lambda/apis/src/catalog-v2/types';
import { ReplacedProduct } from '../collections/catalogue/helpers';
import { ProductDetails } from '@/components/products/PurchaseLinks';
import { OffersT } from '../checkout/types';

export type IdentifyCustomerRequest = {
  userId?: string;
  properties: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    reebelo_id?: string;
    shopify_id?: string;
    store?: ReebeloStoreT;
    unsubscribed?: boolean;
    email_subscribe?: CustomerSubscriptionStatus;
    subscription_source?: CustomerSubscriptionSource;
    checkout_url?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
  };
};

export enum ProductSource {
  CART_RECOMMENDATIONS_SLIDER = 'cart-recommendations-slider',
  CART_RECOMMENDATIONS = 'cart-recommendations',
  ACCESSORIES_PAGE = 'accessories-page',
  CART_SUMMARY = 'cart-summary',
  ACCOUNT = 'account',
  PDP = 'pdp',
  SPECS = 'specs',
  WARRANTY = 'warranty',
  FASTER_DELIVERY = 'faster-delivery',
}

export type ProductEventProperties = {
  offer_id?: string;
  line_item_id?: string;
  product_id?: string;
  sku_id?: string;
  variant_id?: string;
  offer_pk?: string;
  sku?: string;
  psku?: string;
  name?: string;
  category?: string;
  brand?: string;
  color?: string;
  condition?: ReebeloConditions;
  price?: number;
  compare_price?: number;
  currency?: ReebeloCurrencies;
  quantity?: number;
  images?: string[];
  store?: ReebeloStoreT;
  source?: ProductSource;
  url?: string;
  timestamp?: string;
  vendor?: string;
  battery?: {
    upgrade_pct?: number;
    upgrade_price?: number;
  };
  product_type?: string;
  buyer_fee?: number;
  adjustment_factor?: number;
  buyer_fee_adjustment_factor?: number;
  adjusted_value?: number;
  slot_number?: number;
  recommendations?: ProductEventProperties[];
};

export type ReebeloCareTrackRequest = {
  entry_point: 'Product Page' | 'Cart' | 'Cart Pop-up' | 'Account';
  reebelocare_id: string | undefined;
  device_psku: string;
  device_vendor: string;
  device_sku: string;
  duration: RC_DURATION | undefined;
  previous_order_number?: string;
};

export type ProductTrackRequest = {
  v2?: {
    sku: SkuT | null;
    product: ProductT;
    offer: OfferT | null;
    isOutOfStock: boolean;
    slotNumber?: number;
  };
  offer?: ReplacedProduct;
  details?: ProductDetails;
  variant?: ProductVariant;
  cartLine?: CartLineT;
  variantNotes?: NoteT[];
  properties?: ProductEventProperties;
  customer?: Customer;
};

export type CartEventProperties = {
  store: ReebeloStoreT;
  cart_id: string;
  products: ProductEventProperties[];
  /** Set when the event comes from somewhere other than the cart page. */
  source?: ProductSource;
};

export type CartTrackRequest = {
  cart: CartT;
  properties?: CartEventProperties;
};

export type CheckoutEventProperties = {
  store?: ReebeloStoreT;
  order_id?: string;
  checkout_id?: string;
  email?: string;
  phone?: string;
  affiliation?: string;
  value?: number;
  revenue?: number;
  shipping?: number;
  tax?: number;
  duties?: number;
  discount?: number;
  coupon?: string;
  currency?: ReebeloCurrencies;
  products?: ProductEventProperties[];
  new_checkout?: boolean;
  payment_processor?: PaymentProcessor;
  address_entered?: CustomerAddress;
  field_name?: string;
  tax_amount?: number;
  error_message?: string;
  payment_method?: CheckoutPaymentMethods | null;
  adjusted_value?: number;
};

export type CheckoutTrackRequest = {
  cart?: CartT;
  checkout?: CheckoutT;
  order?: CustomerOrderT;
  properties?: CheckoutEventProperties;
  offers?: OffersT;
};
